import Box from '@mui/material/Box';
import type { FC } from 'react';
import { RiPushpinFill, RiPushpinLine } from 'react-icons/ri';

interface PinToggleProps {
  pinned: boolean;
  onChange: (pinned: boolean) => void;
}

/**
 * Compact pin toggle for the post composer / edit form (space admins only).
 * Sits alongside the tag and post-type selects and matches their rectangular
 * shape and borderless soft-grey fill: a quiet neutral pill at rest, a deeper
 * neutral fill with a filled pushpin when the post is set to pin to the top of
 * the feed. Deliberately non-primary — only the Post button reads as primary
 * in the composer.
 */
export const PinToggle: FC<PinToggleProps> = ({ pinned, onChange }) => (
  <Box
    component="button"
    type="button"
    onClick={() => onChange(!pinned)}
    aria-pressed={pinned}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      height: 34,
      px: 1.25,
      borderRadius: '10px',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'Figtree, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      transition: 'background-color 0.15s ease, color 0.15s ease',
      ...(pinned
        ? {
            // A neutral fill marks the active state — no primary color.
            bgcolor: '#eae4e2',
            color: '#5a5350',
            '&:hover': { bgcolor: '#e2dad7' },
          }
        : {
            // Transparent at rest with a soft-grey hover — matches the feed
            // filter trigger buttons and the other composer control pills.
            bgcolor: 'transparent',
            color: '#666',
            '&:hover': { bgcolor: '#f4f4f4' },
          }),
    }}
  >
    {pinned ? <RiPushpinFill size={15} /> : <RiPushpinLine size={15} />}
    {pinned ? 'Pinned' : 'Pin'}
  </Box>
);
